// Minimal, dependency-free markdown -> HTML converter for blog content.
// Supports: headings (#..######), paragraphs, **bold**, *italic*, [links](url),
// ![images](file-or-url), ordered/unordered lists, blockquotes and horizontal rules.
// Raw HTML in the source is escaped, so content from JSON cannot inject markup.

const IMAGE_BASE = "/assets/images/blog/";

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const resolveImage = (src) =>
  /^(https?:)?\/\//.test(src) || src.startsWith("/") ? src : IMAGE_BASE + src;

const safeUrl = (url) => (/^\s*javascript:/i.test(url) ? "#" : url);

const inline = (text) => {
  let out = escapeHtml(text);
  out = out.replace(
    /!\[([^\]]*)\]\(([^)\s]+)\)/g,
    (_, alt, src) =>
      `<img class="img-fluid" src="${resolveImage(src)}" alt="${alt}" />`
  );
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_, label, url) => `<a href="${safeUrl(url)}">${label}</a>`
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  return out;
};

export const markdownToHtml = (markdown = "") => {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const html = [];
  let i = 0;

  const isBlockStart = (l) =>
    /^#{1,6}\s/.test(l) ||
    /^\s*([-*+]|\d+\.)\s+/.test(l) ||
    /^>\s?/.test(l) ||
    /^(-{3,}|\*{3,})\s*$/.test(l);

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const cls = level <= 2 ? "display-6 single-blog__title" : "single-blog__title";
      html.push(`<h${level} class="${cls}">${inline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (/^(-{3,}|\*{3,})\s*$/.test(line)) {
      html.push("<hr />");
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      html.push(
        `<blockquote class="single-blog__para__quote"><div class="single-blog__para__quote__text"><p>${inline(
          quote.join(" ")
        )}</p></div></blockquote>`
      );
      continue;
    }

    const listMatch = line.match(/^\s*([-*+]|\d+\.)\s+/);
    if (listMatch) {
      const ordered = /\d+\./.test(listMatch[1]);
      const items = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*+]|\d+\.)\s+/, ""));
        i++;
      }
      const tag = ordered ? "ol" : "ul";
      html.push(
        `<${tag}>${items.map((t) => `<li>${inline(t)}</li>`).join("")}</${tag}>`
      );
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-block lines.
    const para = [];
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) {
      para.push(lines[i].trim());
      i++;
    }
    const text = para.join(" ");
    html.push(`<p>${inline(text)}</p>`);
  }

  return html.join("\n");
};
